import { useSelector } from 'react-redux';
import type { MouseEvent, VoidFunctionComponent } from 'react';

import {
  BINARY_DECOMPOSITION,
  VENDORED_DEPENDENCY_DETECTION,
} from '../../../../../../../shared/featureFlags/flagNames';
import { type Fetcher, fetcherToPlainText } from '../../../../../../../shared/fetchers';
import { renderRevision } from '../../../../../../../shared/helpers/renderLocator';
import { useIsFeatureEnabled } from '../../../../../../helpers/features';
import { useGetDependencyConfidenceByDependencyScope } from '../../../../../../hooks/api/binary';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import { Badge, BUTTON_TYPE, Icon, icons, TooltipBadge } from '../../../../../generic';
import Button from '../../../../../generic/Button';
import FetcherIcon from '../../../../../generic/FetcherIcon';
import Tooltip from '../../../../../generic/Tooltip';
import TooltipIcon from '../../../../../generic/TooltipIcon';
import BinaryComponentBadge from '../../../../../shared/revisions/BinaryComponentBadge';
import PartialResultGate from '../../../../generic/PartialResult/PartialResultGate';
import { SourceIcon } from '../../../SourceIcon';
import type Locator from '../../../../../../../shared/Locator';
import type { DependencyV2Modals } from '../../../hooks/useDependencyModals';

import styles from '../DependencyRow.module.less';

export type DependencyTitleProps = {
  title: string;
  revision: Locator;
  version?: string;
  depth: number;
  layerDepth?: number;
  isManual: boolean;
  isBlocked: boolean;
  isVendored: boolean;
  isSnippetConversion: boolean;
  hasMalwareIssue: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  modals: DependencyV2Modals;
};

const BlockedBadge: VoidFunctionComponent = () => (
  <Badge color="red">Blocked</Badge>
);

const ManualBadge: VoidFunctionComponent = () => (
  <Badge color="light-blue">Manual Dependency</Badge>
);

const SnippetConversionBadge: VoidFunctionComponent = () => (
  <TooltipBadge
    id="snippet-conversion-badge"
    color="light-yellow"
    icon={icons.arrowRightFromArc}
    tooltip="Manually converted from a snippet"
    fullWidth={false}
    >
    Snippet Conversion
  </TooltipBadge>
);

const MalwareBadge: VoidFunctionComponent = () => (
  <Badge color="dark-red">Malware</Badge>
);

type DepthBadgeProps = {
  isDirect: boolean;
};

export const DepthBadge: VoidFunctionComponent<DepthBadgeProps> = ({ isDirect }) => (
  <Badge color="lighter-gray">{isDirect ? 'Direct' : 'Transitive'}</Badge>
);

type LayerDepthIconProps = {
  isBase: boolean;
};

const LayerDepthIcon: VoidFunctionComponent<LayerDepthIconProps> = ({ isBase }) => (
  <TooltipIcon
    icon={isBase ? icons.sendBackward : icons.layerGroup}
    tooltip={`Layer: ${isBase ? 'Base' : 'Other'} Layer`}
    id={`depth-icon-${isBase ? 'base' : 'other'}`}
  />
);

const DependencyTitle: VoidFunctionComponent<DependencyTitleProps> = ({
  title,
  revision,
  depth,
  layerDepth,
  isBlocked,
  isManual,
  isVendored,
  isSnippetConversion,
  hasMalwareIssue,
  version,
  onClick,
  modals,
}) => {
  const versionTag = version || renderRevision(revision.toParts());
  const fetcher = revision.fetcher.full || '';
  const fetcherName = fetcherToPlainText(fetcher as Fetcher) ?? 'Unknown';

  const scope = useSelector(DependenciesV2Store.selectors.getScope);
  const isBinary = fetcher === 'csbinary';
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const { confidence, isLoading, isEnabled } = useGetDependencyConfidenceByDependencyScope(
    scope,
    revision.toFullString(),
  );

  const shouldShowSnippetConversionBadge = isVendoredDependenciesEnabled && isSnippetConversion;
  const shouldShowManualBadge = isManual && !shouldShowSnippetConversionBadge;

  const openBinaryMatchDetailsModal = () =>
    modals.viewBinaryMatchDetailsModal.onOpen({
      context: { revision: revision.toFullString() },
    });

  return (
    <div className={styles.DependencyTitle}>
      <Button type={BUTTON_TYPE.TEXT} onClick={onClick}>
        <div className={styles.DependencyInfoTitle}>
          <div className={styles.sourceIcon}>
            {isVendoredDependenciesEnabled && <SourceIcon isVendored={isVendored} />}
            {!isVendoredDependenciesEnabled && <Icon icon={icons.boxArchive} />}
          </div>
          <span className={styles.title}>{title}</span>
          <div className={styles.revision}>
            (<span>{versionTag}</span>)
          </div>
          <DepthBadge isDirect={depth <= 1} />
          {layerDepth !== undefined && <LayerDepthIcon isBase={layerDepth === 0} />}
          {isBlocked && <BlockedBadge />}
          {shouldShowManualBadge && <ManualBadge />}
          {shouldShowSnippetConversionBadge && <SnippetConversionBadge />}
          {hasMalwareIssue && <MalwareBadge />}
        </div>
      </Button>
      {isBinary && (
        <BinaryComponentBadge confidence={confidence} isLoading={isLoading} isEnabled={isEnabled} />
      )}
      {isBinary && isBinaryDecompositionEnabled && (
        <PartialResultGate>
          <Button type={BUTTON_TYPE.ANCHOR} onClick={openBinaryMatchDetailsModal}>
            Match Details
          </Button>
        </PartialResultGate>
      )}
      <Button type={BUTTON_TYPE.TEXT} onClick={onClick}>
        <div className={styles.DependencyInfoTitle}>
          <div className={styles.fetcher}>
            <Tooltip id={`${revision}-fetcher`} tooltip={fetcherName}>
              {/* Note that this span is required to enable the tooltip on an SVG. */}
              <span>
                <FetcherIcon fetcher={fetcher} isSmallIcon />
              </span>
            </Tooltip>
          </div>
          <div className={styles.revision}>{revision.toFullString()}</div>
        </div>
      </Button>
    </div>
  );
};

export default DependencyTitle;
